// src/infrastructure/services/api.ts
import { HttpService } from "./http"
import { ApiRoutes } from "./routes"
import { SessionService } from "./session"
import { Logger } from "../services/logger"
import {
  LoginResponse,
  LoginRequest,
  LoginError,
  ChangePasswordResponse,
  ChangePasswordRequest,
} from "../models/login"

import {
  NewUser,
  User,
  UpdateUserResponse,
  UpdateUserRequest,
} from "../models/user"
import { FranchiseRole } from "../models/enums/franchiserole"
import { Terminal } from "models/terminal"
import { MetadataResponse, VendorProduct } from "models/product"
import { GetVendorProductsResponse } from "models/response/GetVendorProductResponse"
import { GetOrderResponse, PaginatedOrderResponse } from "models/response/GetOrdersResponse"
import { container } from "../../infrastructure/container"

/**
 * Interface defining login functionality.
 */
export interface ApiService {
  /**
   * Logs the user in using their credentials.
   * @param email - The user's email address (optional) Required for Email-based login.
   * @param password - The user's password (optional) Required when Email-based login.
  `* @param phone - The user's phone number (optional). Required for OTP-based login.
   * @returns A promise resolving to a `LoginResponse` on success.
   * @throws A structured `LoginError` if authentication fails or input is invalid.
   */

  login(email?: string, password?: string, phone?: string): Promise<LoginResponse>

  /**
   * Verifies the OTP sent to the user's phone during OTP-based login.
   *
   * This method confirms the user's identity using the OTP sent in a prior step.
   *
   * @param phone - The phone number to which the OTP was sent.
   * @param otp - The OTP entered by the user for verification.
   * @returns A promise resolving to a `LoginResponse` on successful OTP verification.
   * @throws A structured `LoginError` if OTP is incorrect, expired, or verification fails.
   */
  verifyOtp(phone: string, otp: string): Promise<LoginResponse>

  /**
   * Sends a password reset email.
   * @param {string} email - The user's email.
   * @returns {Promise<any>} API response for password reset request.
   */
  forgotPassword(email: string): Promise<{ message: string }>

  /**
   * Retrieves terminal details.
   * @param {string} terminalId - The ID of the terminal.
   * @returns {Promise<Terminal>} API response containing terminal details.
   * @throws {Error} If terminal ID is missing.
   */
  getTerminalDetails(terminalId: string): Promise<Terminal>

  /**
   * Creates a new User.
   * @param NewUser - The new User data.
   * @returns A promise resolving to the API response.
   */
  createMember(NewUser: NewUser): Promise<User>

  /**
   * Fetches the list of franchise users from the API.
   * @returns {Promise<User[]>} A promise resolving to an array of franchise users.
   * @throws {LoginError} If the request fails.
   */
  getFranchiseUsers(): Promise<User[]>

  /**
   * Updates an existing User.
   * @param memberData - The updated User data.
   * @returns A promise resolving to the API response.
   */
  updateMember(memberData: UpdateUserRequest): Promise<UpdateUserResponse>

  /**
   * Updates terminal details with provided data.
   * @param terminalData - The updated terminal data.
   * @returns A promise resolving to the API response.
   */
  updateTerminalDetails(userRole: FranchiseRole, updateDetails: Partial<Terminal>): Promise<Terminal>

  /**
   * Changes the user's password.
   * @param currentPassword - The user's current password.
   * @param newPassword - The new password to set.
   * @returns A promise resolving to a success message.
   */
  changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }>

  /**
   * Creates a vendor code for a product.
   * @param vendorProduct - The vendor product data.
   * @returns A promise resolving to the created vendor product.
   */
  createVendorCode(vendorProduct: VendorProduct): Promise<VendorProduct>

  /**
   * Retrieves all vendor-product associations.
   * @returns Promise resolving to array of vendor products
   */
  getVendorProducts(): Promise<GetVendorProductsResponse[]>

  /**
 * Fetches paginated order details for a franchise.
 * Requires a valid `franchiseId`, else throws an error.
 * Includes pagination via `page` and `limit` parameters.
 * Logs errors internally if API request fails.
 * @returns Promise resolving to `PaginatedOrderResponse`.
 */
getOrderDetails(franchiseId: string, page: number, limit: number): Promise<PaginatedOrderResponse>

  /**
   * Logs out the user by calling the backend logout endpoint and clearing session cookies.
   * @returns A promise resolving when the logout process completes.
   */
  logout(): Promise<void>

  /**
   * Fetches metadata including franchise roles and products.
   * @returns A promise resolving to the metadata response.
   */
  getMetadata(): Promise<MetadataResponse>

  /**
   * Fetches order summary for a franchise within an optional date range.
   * @param franchiseId - Required franchise ID
   * @param from - Optional start date (ISO string or yyyy-mm-dd)
   * @param to - Optional end date (ISO string or yyyy-mm-dd)
   * @returns PaginatedOrderResponse containing orders
   * @throws Error if franchiseId is missing
   */
  downloadOrders(franchiseId: string, fromDate?: string, toDate?: string): Promise<{ orders: GetOrderResponse[], deductionPercent: number }>
}

/**
 * Implementation of `LoginService` that interacts with the backend API.
 */
export class AppApiService implements ApiService {
  /**
   * Initializes the login service with the HTTP service instance.
   * @param httpService - The HTTP service to send API requests.
   */
  private httpService = container.resolve<HttpService>("httpService")
  private sessionService = container.resolve<SessionService>("sessionService")
  private logger = container.resolve<Logger>("logger")

  async login(email?: string, password?: string, phone?: string): Promise<LoginResponse> {

    let body: LoginRequest

    // Phone login flow
    if (phone && phone.trim() !== "") {
      body = { phone }
    }

    // Email & password login flow
    if (email && password) {
      body = { email, password }
    }

    const response = await this.httpService.post<LoginRequest, LoginResponse, LoginError>(
      ApiRoutes.LOGIN,
      body
    )

    if (response.user) {
      this.sessionService.user = response.user
    }

    if (response.terminals) {
      this.sessionService.terminals = response.terminals
    }

    if (response.franchise) {
      this.sessionService.franchiseName = response.franchise
    }

    return response
  }

  async verifyOtp(phone: string, otp: string): Promise<LoginResponse> {
    const body = { phone, otp }

    const response = await this.httpService.post<LoginRequest, LoginResponse, LoginError>(
      ApiRoutes.VERIFY_OTP,
      body
    )

    this.sessionService.user = response.user
    this.sessionService.terminals = response.terminals
    this.sessionService.franchiseName = response.franchise

    return response
  }

  async forgotPassword(email: string): Promise<ChangePasswordResponse> {
    const body: LoginRequest = { email }
    return await this.httpService.post<LoginRequest, ChangePasswordResponse, LoginError>
      (
        ApiRoutes.PASSWORD_RESET,
        body
      )
  }

  async getTerminalDetails(terminalId: string): Promise<Terminal> {
    if (!terminalId) {
      throw new Error("Terminal ID is required")
    }

    const response = await this.httpService.get<Terminal, LoginError>(ApiRoutes.TERMINAL_DETAILS, { terminalId })

    return response
  }

  async createMember(newUser: NewUser): Promise<User> {
    try {
      return await this.httpService.post<NewUser, User, LoginError>(
        ApiRoutes.CREATE_MEMBER,
        newUser
      )
    } catch (error) {
      this.logger.error(" Error creating User:", error)
      throw error
    }
  }

  async updateMember(memberData: UpdateUserRequest): Promise<UpdateUserResponse> {
    try {
      return await this.httpService.post<UpdateUserRequest, UpdateUserResponse, LoginError>
        (
          ApiRoutes.UPDATE_MEMBER,
          memberData
        )
    } catch (error) {
      this.logger.error("Error updating User:", error)
      throw error
    }
  }

  async getFranchiseUsers(): Promise<User[]> {
    try {
      const response = await this.httpService.get<User[], LoginError>(ApiRoutes.FRANCHISE_USER)
      this.sessionService.franchiseUsers = response
      return response
    } catch (error) {
      this.logger.error(" Error fetching franchise users:", error)
      throw error
    }
  }

  async updateTerminalDetails(userRole: FranchiseRole, updateDetails: Partial<Terminal>): Promise<Terminal> {
    try {
      const filteredUpdates = this.getUpdatableFields(userRole, updateDetails)
      return await this.httpService.post<Partial<Terminal>, Terminal, LoginError>
        (
          ApiRoutes.UPDATE_TERMINAL,
          filteredUpdates
        )
    } catch (error) {
      this.logger.error(" Error updating terminal:", error)
      throw error
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<ChangePasswordResponse> {
    try {
      const body: ChangePasswordRequest = { currentPassword, newPassword }

      return await this.httpService.post<ChangePasswordRequest, ChangePasswordResponse, LoginError>
        (
          ApiRoutes.CHANGE_PASSWORD,
          body
        )
    } catch (error) {
      this.logger.error("Error changing password:", error)
      throw error
    }
  }

  async logout(): Promise<void> {
    try {
      await this.httpService.post(ApiRoutes.LOGOUT, {})
      this.sessionService.resetStorage()
    } catch (error) {
      this.logger.error("Error logging out:", error)
      throw error
    }
  }

  async getMetadata(): Promise<MetadataResponse> {
    try {
      return await this.httpService.get<MetadataResponse, LoginError>(ApiRoutes.META_DATA)
    } catch (error) {
      this.logger.error("Error fetching metadata:", error)
      throw error
    }
  }

  async createVendorCode(vendorProduct: VendorProduct): Promise<VendorProduct> {
    try {
      const response = await this.httpService.post<VendorProduct, VendorProduct, LoginError>
        (
          ApiRoutes.VENDOR_CODE,
          vendorProduct
        )
      this.sessionService.vendorProduct = response

      return response
    } catch (error) {
      this.logger.error("Error creating vendor code:", error)
      throw error
    }
  }

  async getVendorProducts(): Promise<GetVendorProductsResponse[]> {
    try {
      return await this.httpService.get<GetVendorProductsResponse[], LoginError>(
        ApiRoutes.VENDOR_PRODUCTS
      )
    } catch (error) {
      this.logger.error("Error fetching vendor products:", error)
      throw error
    }
  }

  async getOrderDetails(franchiseId: string, page: number, limit: number | null): Promise<PaginatedOrderResponse> {
    if (!franchiseId) {
      throw new Error("Franchise Id is required")
    }

    try {
      const base = `${ApiRoutes.ORDER_DETAILES}/${franchiseId}`
      const params = new URLSearchParams()
      params.set("page", String(page))
      if (limit !== null) params.set("limit", String(limit))

      const url = `${base}?${params.toString()}`
      return await this.httpService.get<PaginatedOrderResponse, LoginError>(url);
    } catch (error) {
      this.logger.error("Error fetching order details:", error)
      throw error
    }
  }

  async downloadOrders(franchiseId: string, fromDate?: string, toDate?: string): Promise<{ orders: GetOrderResponse[], deductionPercent: number }> {
    if (!franchiseId) {
      throw new Error("Franchise Id is required")
    }

    try {
      const queryParams = new URLSearchParams()
      if (fromDate && fromDate !== "undefined") {
        queryParams.append("from", fromDate)
      }
      if (toDate && toDate !== "undefined") {
        queryParams.append("to", toDate)
      }

      const queryString = queryParams.toString()
      const url = queryString
        ? `${ApiRoutes.DOWNLOAD_ORDERS}/${franchiseId}?${queryString}`
        : `${ApiRoutes.DOWNLOAD_ORDERS}/${franchiseId}`

      const result = await this.httpService.get<{ orders: GetOrderResponse[], deductionPercent: number }, LoginError>(url)
      return result
    } catch (error) {
      this.logger.error("Error fetching order details:", error)
      throw error
    }
  }

  /**
   * Filters terminal update fields based on the user's role permissions.
   * 
   * This implements role-based access control for terminal updates:
   * - Managers can only update pricing fields
   * - Owners/Admins can update all fields
   * - Other roles can't update any terminal fields
   * 
   * @param userRole - The role of the user attempting the update
   * @param updateDetails - The proposed terminal updates
   * @returns A filtered object containing only allowed fields for the role
   */
  private getUpdatableFields(userRole: FranchiseRole, updateDetails: Partial<Terminal>): Partial<Terminal> {
    const filteredUpdates: Partial<Terminal> = { terminalId: updateDetails.terminalId }

    if (userRole === FranchiseRole.MANAGER) {
      return {
        ...filteredUpdates,
        ...(updateDetails.basePrice && { basePrice: updateDetails.basePrice }),
        ...(updateDetails.additionalPrices && { additionalPrices: updateDetails.additionalPrices }),
        ...(updateDetails.bluetoothMacId && { bluetoothMacId: updateDetails.bluetoothMacId }),
      }
    }

    if ([FranchiseRole.OWNER, FranchiseRole.ADMIN].includes(userRole)) {
      const allowedFields = ["name", "status", "basePrice", "additionalPrices", "lockers", "bluetoothMacId"]
      return {
        ...filteredUpdates,
        ...Object.fromEntries(
          Object.entries(updateDetails).filter(([key]) => allowedFields.includes(key))
        ),
      }
    }
    return filteredUpdates
  }
}